import { useContext, useEffect, useState, useRef, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { useParams } from 'react-router-dom'
import uuid from 'uuid/v4'
import { CreateDatasetContext } from '@datasets/reducer/context'
import { getDatasetsQuery } from '@global/api'
import { transformClassificationsIntoColumns } from '@global/api/transforms'
import { IDatasetMetadataResponse, IDatasetQueryResponse } from '@global/api/types'
import { useCreativeClassifications } from '@global/hooks/queries/useCreativeClassifications'
import { useAppDispatch, useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { DataContainer } from 'dataContainer/data-container'
import { showSnackbar } from 'functions'
import { UrlAudits } from 'handsontable/utils/codes.url-audits'
import * as DatasetActions from '../datasets.actions'
import { makeDatasetFromQueryData } from '../utils'

export const KEY_DATASET_DATA_QUERY = 'dataset-data-query'

/**
 * Fetch dataset data and metadata.
 * - Call the query and metadata endpoints, to update the Data Container with the fetched dataset.
 * - Update redux store with fetched data.
 * - Set datasetuuid state for managing new or existing datasets.
 */
interface UseQueryDatasetDataProps {
  templateId: string
  binderRef: any
  enabled?: boolean
}

interface UseQueryDatasetDataRouterParams {
  datasetId: string
}

export const useQueryDatasetData = ({
  templateId,
  binderRef,
  enabled = true,
}: UseQueryDatasetDataProps) => {
  const dispatch = useAppDispatch()
  const { datasetId } = useParams<UseQueryDatasetDataRouterParams>()
  const dataContainerRef = useRef<DataContainer | null>(null)
  const processedDataRef = useRef(false)
  const submissionTemplate = useAppSelector(
    state => state.Datasets.submissionTemplatesById[templateId],
  )
  const { state, dispatch: dispatchState } = useContext(CreateDatasetContext)
  const [datasetUuid, setDatasetUuid] = useState(uuid())
  const { subTitle, submissionName, dataContainer, dataset } = state

  const { data: classifications = [] } = useCreativeClassifications()

  const transformedColumns = submissionTemplate?.transformedColumns
  const creativeConfiguration = submissionTemplate?.creativeConfiguration

  /**
   * Initialize the data container with the transformed columns and classifications.
   * - If the data container is already initialized, return it.
   */
  const initializeDataContainer = useCallback(() => {
    if (Object.keys(dataContainer).length) {
      return dataContainer
    }

    return new DataContainer([
      ...transformedColumns,
      /**
       * If classifications are available, transform them into columns and add them to the data container.
       * This is needed because in the create dataset context, we don't run the `transforms` we do in the
       * DatasetData flow.
       */
      ...transformClassificationsIntoColumns({
        creativeConfiguration,
        classifications,
        toSnakeCase: true,
      }),
    ])
  }, [transformedColumns, creativeConfiguration, classifications])

  // Initialize data container once.
  useEffect(() => {
    if (dataContainerRef.current) return
    if (!submissionTemplate) return

    dataContainerRef.current = initializeDataContainer()

    if (dataContainerRef.current) {
      dataContainerRef.current.datasetUuid = datasetUuid

      dispatchState({
        type: 'setDataContainer',
        payload: dataContainerRef.current,
      })
      dispatchState({
        type: 'setLoading',
        payload: false,
      })
    }
  }, [submissionTemplate, initializeDataContainer, datasetUuid])

  const {
    data: datasetDataQuery,
    isLoading,
    isFetched,
  } = useQuery({
    queryKey: [KEY_DATASET_DATA_QUERY, datasetId],
    queryFn: ({ signal }) =>
      getDatasetsQuery({
        datasetId,
        queryOptions: {},
        axiosOptions: { signal },
        errorCallback: error => {
          dispatch(
            showSnackbar({
              message: error.message,
              type: 'ERROR',
              duration: 5000,
            }),
          )
        },
      }),
    enabled: enabled && !!datasetId && Boolean(submissionTemplate), // FIXME: What condition is submissionTemplate not available?
    refetchOnWindowFocus: false,
    cacheTime: datasetId ? 5 * 60 * 1000 : 0,
    select: useCallback(
      (data: IDatasetQueryResponse) => {
        const transformedData = makeDatasetFromQueryData(data)

        if (dataContainerRef.current) {
          // Keep the dataContainer in sync with the updated dataset.
          dataContainerRef.current.updateDatasetDataAndTemplateFields({
            binderRef: binderRef.current,
            dataset: transformedData,
            transformedColumns: submissionTemplate?.transformedColumns,
            afterUpdateCallback: null,
          })
          UrlAudits.set(transformedData.link_validation_results)
          UrlAudits.pollPending(transformedData.uuid, dataContainerRef.current)
          UrlAudits.validateUncompleted(
            transformedData,
            submissionTemplate,
            dataContainerRef.current,
          )
        }

        return transformedData
      },
      [submissionTemplate, datasetUuid],
    ),
  })

  const { data: metadata, isLoading: isMetadataLoading } = useQuery({
    queryKey: ['dataset-data-metadata', datasetId],
    queryFn: async () => {
      const { data } = await api.get<IDatasetMetadataResponse>(
        `/api/v1/account/dataset/${datasetId}/metadata`,
      )
      return data
    },
    refetchOnWindowFocus: false,
    enabled: enabled && !!datasetId,
  })

  const isDatasetEmpty = isEmpty(dataset)

  // Update the data container and redux dataset, after fetching the dataset.
  useEffect(() => {
    if (isLoading || !datasetDataQuery || !isFetched) {
      processedDataRef.current = false
      return
    }

    if (
      !isLoading &&
      datasetDataQuery &&
      isFetched &&
      dataContainerRef.current &&
      !processedDataRef.current
    ) {
      dataContainerRef.current.updateDatasetDataAndTemplateFields({
        binderRef,
        dataset: datasetDataQuery,
        transformedColumns: submissionTemplate?.transformedColumns,
        afterUpdateCallback: null,
      })

      dispatchState({
        type: 'afterDatasetDataUpdate',
        payload: {
          dataContainer: dataContainerRef.current,
          dataset: datasetDataQuery,
          subTitle,
          submissionName,
        },
      })

      processedDataRef.current = true
    }
  }, [
    isLoading,
    datasetDataQuery,
    isFetched,
    isDatasetEmpty,
    subTitle,
    submissionName,
    templateId,
    submissionTemplate,
  ])

  // metadata effect
  useEffect(() => {
    if (isMetadataLoading) return
    if (!metadata) return

    dispatch(DatasetActions.setDatasetMetadata(metadata))
    dispatchState({ type: 'setSubmissionName', payload: metadata.identifier })
    if (metadata.uuid) {
      setDatasetUuid(metadata.uuid)
    }
  }, [metadata, isMetadataLoading])

  return {
    datasetUuid,
  }
}
